import React, { useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import L from 'leaflet';
import { MapContainer, Polygon, Popup, TileLayer, useMap } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

import { FieldSummary } from '../../../models/FieldsSummary';

const STATUS_COLORS: Record<string, string> = {
    ok: '#2e7d32',
    warn: '#d68910',
    none: '#8c8c8c',
};

interface Props {
    fields: FieldSummary[];
}

/** Полигоны одного поля: FeatureCollection → массив колец [lat, lng][] */
const fieldRings = (geoJson: string): L.LatLngExpression[][][] => {
    if (!geoJson) return [];
    let parsed: any;
    try {
        parsed = JSON.parse(geoJson);
    } catch {
        return [];
    }
    const features = parsed?.type === 'FeatureCollection' ? parsed.features || [] : [parsed];
    const result: L.LatLngExpression[][][] = [];

    const toLatLng = (coords: number[][]): L.LatLngExpression[] =>
        coords.map((c) => [c[1], c[0]] as L.LatLngExpression);

    features.forEach((f: any) => {
        const geom = f?.geometry;
        if (!geom) return;
        if (geom.type === 'Polygon') {
            result.push(geom.coordinates.map(toLatLng));
        } else if (geom.type === 'MultiPolygon') {
            geom.coordinates.forEach((poly: number[][][]) => {
                result.push(poly.map(toLatLng));
            });
        }
    });

    return result;
};

const FitBounds: React.FC<{ fields: FieldSummary[] }> = ({ fields }) => {
    const map = useMap();
    useEffect(() => {
        const points: L.LatLngExpression[] = [];
        fields.forEach((f) => {
            fieldRings(f.geo_json).forEach((rings) => {
                rings.forEach((ring) => points.push(...ring));
            });
        });
        if (points.length > 0) {
            map.fitBounds(L.latLngBounds(points as L.LatLngTuple[]), { padding: [24, 24], maxZoom: 14 });
        }
    }, [fields, map]);
    return null;
};

const FieldsMap: React.FC<Props> = ({ fields }) => {
    const navigate = useNavigate();

    const defaultCenter = useMemo<L.LatLngExpression>(() => [56.47, 84.54], []);

    return (
        <div style={{ height: 340, borderRadius: 12, overflow: 'hidden', border: '1px solid #eef0f2' }}>
            <MapContainer center={defaultCenter} zoom={10} style={{ height: '100%', width: '100%' }} attributionControl={false}>
                <TileLayer
                    
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds fields={fields} />
                {fields.map((f) =>
                    fieldRings(f.geo_json).map((rings, idx) => (
                        <Polygon
                            key={`${f.id}-${idx}`}
                            positions={rings as L.LatLngExpression[][]}
                            pathOptions={{
                                color: STATUS_COLORS[f.status] || STATUS_COLORS.none,
                                weight: 2,
                                fillOpacity: 0.2,
                            }}
                            eventHandlers={{ click: () => navigate(`/fields/${f.id}`) }}
                        >
                            <Popup>
                                <b>{f.name || `Поле №${f.id}`}</b>
                                <br />
                                {f.season?.culture_name || 'Культура не задана'}
                                <br />
                                {f.area_ga ? `${f.area_ga.toFixed(1)} га` : ''}
                            </Popup>
                        </Polygon>
                    )),
                )}
            </MapContainer>
        </div>
    );
};

export default FieldsMap;
